import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import './N8NMonitorPage.css';

function N8NMonitorPage() {
  const { theme, toggleTheme } = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'running', 'error', 'waiting'

  useEffect(() => {
    document.title = 'PEC - Monitor';
  }, []);

  // Mock automation data
  const automations = [
    {
      id: 1,
      name: 'Email Processing Pipeline',
      status: 'running',
      lastRun: '2024-01-15T10:30:00Z',
      nextRun: '2024-01-15T11:00:00Z',
      executions: 1247,
      successRate: 98.2,
      category: 'Email',
      triggers: ['Email received', 'Schedule'],
      description: 'Automatiza el procesamiento y clasificación de emails entrantes',
      error: null
    },
    {
      id: 2,
      name: 'Financial Data Sync',
      status: 'success',
      lastRun: '2024-01-15T09:45:00Z',
      nextRun: '2024-01-15T12:00:00Z',
      executions: 892,
      successRate: 99.1,
      category: 'Finance',
      triggers: ['Schedule', 'Webhook'],
      description: 'Sincroniza datos financieros con servicios externos',
      error: null
    },
    {
      id: 3,
      name: 'Task Reminder System',
      status: 'error',
      lastRun: '2024-01-15T08:15:00Z',
      nextRun: '2024-01-15T09:00:00Z',
      executions: 456,
      successRate: 94.7,
      category: 'Productivity',
      triggers: ['Schedule'],
      description: 'Envía recordatorios automáticos de tareas pendientes',
      error: 'Connection timeout to notification service'
    },
    {
      id: 4,
      name: 'Social Media Monitor',
      status: 'waiting',
      lastRun: '2024-01-14T23:30:00Z',
      nextRun: '2024-01-15T12:30:00Z',
      executions: 324,
      successRate: 96.8,
      category: 'Marketing',
      triggers: ['Webhook', 'Schedule'],
      description: 'Monitorea menciones y engagement en redes sociales',
      error: null
    },
    {
      id: 5,
      name: 'Backup Automation',
      status: 'running',
      lastRun: '2024-01-15T02:00:00Z',
      nextRun: '2024-01-16T02:00:00Z',
      executions: 128,
      successRate: 100,
      category: 'System',
      triggers: ['Schedule'],
      description: 'Realiza copias de seguridad automáticas de datos críticos',
      error: null
    },
    {
      id: 6,
      name: 'Weather Data Collector',
      status: 'success',
      lastRun: '2024-01-15T10:00:00Z',
      nextRun: '2024-01-15T13:00:00Z',
      executions: 2156,
      successRate: 97.4,
      category: 'Data',
      triggers: ['Schedule', 'API'],
      description: 'Recopila y procesa datos meteorológicos para análisis',
      error: null
    }
  ];

  // Filter automations
  const filteredAutomations = automations.filter(automation => {
    const matchesSearch = automation.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         automation.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         automation.category.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = activeFilter === 'all' || automation.status === activeFilter;
    
    return matchesSearch && matchesFilter;
  });

  // Calculate stats
  const stats = {
    total: automations.length,
    running: automations.filter(a => a.status === 'running').length,
    errors: automations.filter(a => a.status === 'error').length,
    success: automations.filter(a => a.status === 'success').length,
    waiting: automations.filter(a => a.status === 'waiting').length,
    avgSuccessRate: (automations.reduce((sum, a) => sum + a.successRate, 0) / automations.length).toFixed(1)
  };

  const handleAutomationAction = (automationId, action) => {
    console.log(`${action} automation:`, automationId);
    // Implement automation actions
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running': return 'blue';
      case 'success': return 'green';
      case 'error': return 'red';
      case 'waiting': return 'orange';
      default: return 'gray';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'running': return '🔄';
      case 'success': return '✅';
      case 'error': return '❌';
      case 'waiting': return '⏳';
      default: return '⚪';
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'Email': '📧',
      'Finance': '💰',
      'Productivity': '✅',
      'Marketing': '📱',
      'System': '🔧',
      'Data': '📊'
    };
    return icons[category] || '⚙️';
  };

  return (
    <div className="n8n-page">
      <div className="n8n-header">
        <div className="header-content">
          <div className="header-left">
            <h1>Monitor de Automatización</h1>
            <p>Supervisa y gestiona tus flujos de trabajo automatizados</p>
          </div>
          <div className="header-center">
            <div className="search-container">
              <div className="search-input-wrapper">
                <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M21 21L16.514 16.506M19 10.5A8.5 8.5 0 1110.5 2a8.5 8.5 0 018.5 8.5z" 
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <input
                  type="search"
                  placeholder="Buscar automatizaciones, categorías..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>
            </div>
          </div>
          <div className="header-right">
            <button className="create-action-button">
              ➕ NUEVO FLUJO
            </button>
            <button 
              onClick={toggleTheme}
              className="theme-toggle" 
              title={`Cambiar a tema ${theme === 'light' ? 'oscuro' : 'claro'}`}
            >
              {theme === 'light' ? '🌙' : '☀️'}
            </button>
          </div>
        </div>
      </div>

      <div className="n8n-content">
        {/* Automation Stats */}
        <div className="n8n-stats">
          <div className="stat-card total">
            <div className="stat-icon">⚙️</div>
            <div className="stat-content">
              <h3>{stats.total}</h3>
              <p>Total Automatizaciones</p>
            </div>
          </div>
          <div className="stat-card running">
            <div className="stat-icon">🔄</div>
            <div className="stat-content">
              <h3>{stats.running}</h3>
              <p>En Ejecución</p>
            </div>
          </div>
          <div className="stat-card errors">
            <div className="stat-icon">❌</div>
            <div className="stat-content">
              <h3>{stats.errors}</h3>
              <p>Con Errores</p>
            </div>
          </div>
          <div className="stat-card success">
            <div className="stat-icon">📊</div>
            <div className="stat-content">
              <h3>{stats.avgSuccessRate}%</h3>
              <p>Tasa de Éxito</p>
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="automation-filters">
          <div className="filter-tabs">
            <button 
              className={`filter-tab ${activeFilter === 'all' ? 'active' : ''}`}
              onClick={() => setActiveFilter('all')}
            >
              🌐 Todas ({stats.total})
            </button>
            <button 
              className={`filter-tab ${activeFilter === 'running' ? 'active' : ''}`}
              onClick={() => setActiveFilter('running')}
            >
              🔄 Ejecutando ({stats.running})
            </button>
            <button 
              className={`filter-tab ${activeFilter === 'success' ? 'active' : ''}`}
              onClick={() => setActiveFilter('success')}
            >
              ✅ Exitosas ({stats.success})
            </button>
            <button 
              className={`filter-tab ${activeFilter === 'error' ? 'active' : ''}`}
              onClick={() => setActiveFilter('error')}
            >
              ❌ Con Error ({stats.errors})
            </button>
          </div>
          <div className="filter-actions">
            <button className="btn-secondary">Refrescar</button>
            <button className="btn-secondary">Exportar</button>
            <button className="btn-secondary">Configurar</button>
          </div>
        </div>

        {/* Automations List */}
        <div className="automations-section">
          <div className="section-header">
            <h2>Flujos de Trabajo ({filteredAutomations.length})</h2>
            <div className="status-indicator">
              <span className="system-status">🟢 Sistema Operativo</span>
            </div>
          </div>

          <div className="automations-list">
            {filteredAutomations.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">⚙️</div>
                <h3>No se encontraron automatizaciones</h3>
                <p>Intenta ajustar los términos de búsqueda o filtros</p>
              </div>
            ) : (
              filteredAutomations.map((automation) => (
                <div key={automation.id} className={`automation-card ${getStatusColor(automation.status)}`}>
                  <div className="automation-header">
                    <div className="automation-info">
                      <div className="automation-title-row">
                        <div className="automation-category">
                          {getCategoryIcon(automation.category)}
                        </div>
                        <h4 className="automation-name">{automation.name}</h4>
                        <div className="automation-status">
                          <span className={`status-badge ${getStatusColor(automation.status)}`}>
                            {getStatusIcon(automation.status)} {automation.status.toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <p className="automation-description">{automation.description}</p>
                      <div className="automation-details">
                        <span className="detail-item">📊 {automation.executions} ejecuciones</span>
                        <span className="detail-item">✅ {automation.successRate}% éxito</span>
                        <span className="detail-item">🕐 Última: {formatDateTime(automation.lastRun)}</span>
                        <span className="detail-item">⏰ Próxima: {formatDateTime(automation.nextRun)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="automation-body">
                    <div className="automation-triggers">
                      <span className="triggers-label">Triggers:</span>
                      {automation.triggers.map((trigger, index) => (
                        <span key={index} className="trigger-tag">
                          {trigger}
                        </span>
                      ))}
                    </div>
                    
                    {automation.error && (
                      <div className="automation-error">
                        <span className="error-icon">⚠️</span>
                        <span className="error-message">{automation.error}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="automation-actions">
                    <button 
                      className="btn-action primary"
                      onClick={() => handleAutomationAction(automation.id, 'view')}
                    >
                      Ver Detalles
                    </button>
                    {automation.status === 'error' ? (
                      <button 
                        className="btn-action secondary"
                        onClick={() => handleAutomationAction(automation.id, 'retry')}
                      >
                        🔄 Reintentar
                      </button>
                    ) : (
                      <button 
                        className="btn-action secondary"
                        onClick={() => handleAutomationAction(automation.id, 'pause')}
                      >
                        ⏸️ Pausar
                      </button>
                    )}
                    <button 
                      className="btn-action secondary"
                      onClick={() => handleAutomationAction(automation.id, 'logs')}
                    >
                      📋 Logs
                    </button>
                    <button 
                      className="btn-action secondary"
                      onClick={() => handleAutomationAction(automation.id, 'edit')}
                    >
                      ✏️ Editar
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="quick-actions-section">
          <h2>Gestión de Automatización</h2>
          <div className="quick-actions-grid">
            <div className="quick-action-card">
              <div className="action-icon">🔗</div>
              <h3>Conectar n8n</h3>
              <p>Configurar conexión con instancia n8n</p>
              <button className="btn-primary">Configurar</button>
            </div>
            <div className="quick-action-card">
              <div className="action-icon">📊</div>
              <h3>Analytics</h3>
              <p>Ver estadísticas de rendimiento</p>
              <button className="btn-primary">Ver Reportes</button>
            </div>
            <div className="quick-action-card">
              <div className="action-icon">🔔</div>
              <h3>Alertas</h3>
              <p>Configurar notificaciones de errores</p>
              <button className="btn-primary">Configurar</button>
            </div>
            <div className="quick-action-card">
              <div className="action-icon">🛠️</div>
              <h3>Plantillas</h3>
              <p>Crear flujos desde plantillas</p>
              <button className="btn-primary">Explorar</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default N8NMonitorPage;
